import { formatTicker } from '@api/mappers/formatTicker';
import {
  isTrustedInsightUrl,
  Mention,
  mentionTypeToChipKind,
} from '@design/chat/mention-composer';
import { MentionChip } from '@design/components/mention-chip/MentionChip';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { ThemeSearchLogo } from '@terminal/components/theme-search-logo/ThemeSearchLogo';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { AssetOverviewPreviewCard } from '@terminal/views/alfred/research/asset-overview-link/AssetOverviewPreviewCard';
import { ThemeOverviewPreviewCard } from '@terminal/views/alfred/research/theme-overview-link/ThemeOverviewPreviewCard';
import type { ReactNode } from 'react';

import styles from './ResolvedMentionChip.module.scss';

export interface ResolvedMentionChipProps {
  mention: Mention;
}

// `id` can come from a hand-typed or model-produced marker, not just our
// own paste flow, so re-validate it's still one of our own URLs before
// linking out.
const InsightMentionChip = ({ id, chip }: { id: string; chip: ReactNode }) =>
  isTrustedInsightUrl(id) ? (
    <a
      href={id}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.previewTrigger}
    >
      {chip}
    </a>
  ) : (
    chip
  );

const isMentionMissing = ({
  type,
  entityData,
  isThemeError,
  isListMissing,
}: {
  type: Mention['type'];
  entityData: ReturnType<typeof useEntity>['data'];
  isThemeError: boolean;
  isListMissing: boolean;
}): boolean => {
  if (type === 'entity') {
    return Boolean(entityData && !entityData.data && !entityData.error);
  }

  return type === 'theme' ? isThemeError : isListMissing;
};

const useIsListMissing = (type: Mention['type'], id: string): boolean =>
  useWatchlist(
    state => type === 'list' && !state.lists.some(list => list.id === id)
  );

export const ResolvedMentionChip = ({
  mention: { type, id, label },
}: ResolvedMentionChipProps) => {
  const isEntity = type === 'entity';
  const isTheme = type === 'theme';

  const { data: entityData } = useEntity(isEntity ? id : '');
  const { data: themeData, isError: isThemeError } = useTheme({
    themeId: isTheme ? id : '',
  });
  const isListMissing = useIsListMissing(type, id);

  if (isMentionMissing({ type, entityData, isThemeError, isListMissing })) {
    return <>{label}</>;
  }

  const entityAvatarUrl = isEntity
    ? entityData?.data?.logo_urls.light_png
    : undefined;

  const avatar = isTheme ? (
    <ThemeSearchLogo
      name={label}
      logoUrl={themeData?.logo_url}
      themeType={themeData?.type}
      size={16}
    />
  ) : entityAvatarUrl ? (
    <img
      src={entityAvatarUrl}
      alt=""
      className={styles.entityAvatar}
      data-testid="mention-chip-avatar"
    />
  ) : undefined;

  // Prefer live entity formatting (`TSLA:NASD`) when available so pills show
  // the exchange suffix even if the stored mention label was ticker-only.
  const displayLabel =
    isEntity && entityData?.data?.ticker
      ? formatTicker(entityData.data)
      : label;

  // Insight mentions carry their share URL as `id` — link straight to the
  // insight instead of the entity/theme hover preview flow.
  if (type.startsWith('insight')) {
    const chip = (
      <MentionChip
        kind={mentionTypeToChipKind(type)}
        label={displayLabel}
        avatar={avatar}
        readOnly
      />
    );
    return <InsightMentionChip id={id} chip={chip} />;
  }

  const previewCard = isEntity ? (
    <AssetOverviewPreviewCard entityPath={id} />
  ) : isTheme ? (
    <ThemeOverviewPreviewCard themeId={id} />
  ) : null;

  const hasPreviewCard = Boolean(previewCard && id);

  const chip = (
    <MentionChip
      kind={mentionTypeToChipKind(type)}
      label={displayLabel}
      avatar={avatar}
      readOnly
      showLabelTooltip={!hasPreviewCard}
    />
  );

  if (!hasPreviewCard) {
    return chip;
  }

  return (
    <TooltipContainer
      label={previewCard}
      trigger={[TooltipTrigger.Hover, TooltipTrigger.Focus]}
      placement="top"
      inPortal
      strategy="fixed"
      withSafePolygon
      hasMaxWidth={false}
      hasArrow={false}
    >
      <span className={styles.previewTrigger} tabIndex={0}>
        {chip}
      </span>
    </TooltipContainer>
  );
};
